import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUserCircle,
  FaSignOutAlt,
  FaBell,
  FaChevronDown,
  FaSearch,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import logo from "../../Assets/Logos/Bharat Karigari N.png";

const AuthNavbar = ({ setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const { user, logout, isAdmin, isSupplier } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const handleLogout = () => {
    logout();
    if (isAdmin) {
      navigate("/admin-login");
    } else {
      navigate("/");
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    setIsNotificationsOpen(false);
  };

  const toggleNotifications = () => {
    setIsNotificationsOpen(!isNotificationsOpen);
    setIsDropdownOpen(false);
  };

  const getUserDisplayName = () => {
    if (isAdmin) {
      return user?.fullName || user?.name || "Admin User";
    }
    if (isSupplier) {
      return user?.businessName || user?.fullName || "Seller";
    }
    return "User";
  };

  const getUserRole = () => {
    if (isAdmin) return "Administrator";
    if (isSupplier) return "Seller";
    return "User";
  };

  const getUserEmail = () => {
    return user?.email || user?.emailId || "user@example.com";
  };

  const getUserInitial = () => {
    return getUserDisplayName().charAt(0).toUpperCase();
  };

  // Mock notifications data
  const notifications = [
    {
      id: 1,
      text: "New order received",
      time: "5 min ago",
      unread: true,
      type: "order",
    },
    {
      id: 2,
      text: "Product low in stock",
      time: "1 hour ago",
      unread: true,
      type: "inventory",
    },
    {
      id: 3,
      text: "System update completed",
      time: "2 hours ago",
      unread: false,
      type: "system",
    },
  ];

  const unreadCount = notifications.filter((n) => n.unread).length;

  const getNotificationIcon = (type) => {
    switch (type) {
      case "order":
        return "🛒";
      case "inventory":
        return "📦";
      case "system":
        return "⚙️";
      default:
        return "🔔";
    }
  };

  const getProfileRoute = () => {
    if (isAdmin) return "/profile";
    if (isSupplier) return "/seller/profile";
    return "/profile";
  };

  return (
    <React.Fragment>
      <nav className="bg-white border-b border-gray-200 fixed top-0 w-full z-50">
        <div className="px-6 mx-auto">
          <div className="flex items-center justify-between h-16">
            {/* Left Section */}
            <div className="flex items-center space-x-4">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="flex items-center space-x-3 cursor-pointer"
                onClick={() => setIsSidebarOpen(true)}
              >
                <img
                  src={logo}
                  alt="Company Logo"
                  className="h-12 w-24 object-contain"
                />
                <div className="hidden md:block border-l border-gray-300 pl-3">
                  <p className="text-sm font-medium text-gray-900">
                    {getUserDisplayName()}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {getUserRole().toLowerCase()}
                  </p>
                </div>
              </motion.div>
            </div>
            {/* Right Section */}
            <div className="flex items-center space-x-3">
              {/* Mobile Search Toggle */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors md:hidden"
              >
                <FaSearch size={16} className="text-gray-600" />
              </motion.button>

              {/* Notifications */}
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleNotifications}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors relative"
                >
                  <FaBell size={18} className="text-gray-600" />
                  {unreadCount > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium border-2 border-white"
                    >
                      {unreadCount}
                    </motion.span>
                  )}
                </motion.button>

                {/* Notifications Dropdown */}
                <AnimatePresence>
                  {isNotificationsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50"
                    >
                      <div className="p-4 border-b border-gray-200 bg-gray-50">
                        <div className="flex justify-between items-center">
                          <h3 className="font-semibold text-gray-800">
                            Notifications
                          </h3>
                          <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                            {unreadCount} new
                          </span>
                        </div>
                      </div>

                      <div className="max-h-96 overflow-y-auto">
                        {notifications.map((notification, index) => (
                          <motion.div
                            key={notification.id}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${notification.unread
                                ? "bg-blue-50 border-l-4 border-l-blue-500"
                                : ""
                              }`}
                          >
                            <div className="flex items-start space-x-3">
                              <span className="text-lg">
                                {getNotificationIcon(notification.type)}
                              </span>
                              <div className="flex-1">
                                <p className="text-sm text-gray-800 font-medium">
                                  {notification.text}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {notification.time}
                                </p>
                              </div>
                              {notification.unread && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>

                      <div className="p-3 bg-gray-50 border-t border-gray-200">
                        <button className="w-full text-center text-sm text-blue-600 font-medium hover:text-blue-800 transition-colors">
                          View All Notifications
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* User Profile */}
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleDropdown}
                  className="flex items-center space-x-3 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <div className="hidden sm:block text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {getUserDisplayName()}
                      </p>
                      <p className="text-xs text-gray-500 capitalize">
                        {getUserRole().toLowerCase()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {getUserInitial()}
                        </span>
                      </div>
                      <motion.div
                        animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <FaChevronDown size={12} className="text-gray-500" />
                      </motion.div>
                    </div>
                  </div>
                </motion.button>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {isDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50"
                    >
                      {/* User Info */}
                      <div className="p-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-lg font-bold">
                              {getUserInitial()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold truncate">
                              {getUserDisplayName()}
                            </p>
                            <p className="text-sm text-gray-300 truncate">
                              {getUserEmail()}
                            </p>
                            <div className="flex items-center mt-1">
                              <span className="inline-block px-2 py-1 text-xs bg-blue-500/20 text-blue-200 rounded-full">
                                {getUserRole()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        <motion.div
                          whileHover={{
                            backgroundColor: "rgba(59, 130, 246, 0.05)",
                          }}
                        >
                          <Link
                            to={getProfileRoute()}
                            onClick={() => setIsDropdownOpen(false)}
                            className="flex items-center px-4 py-3 text-gray-700 hover:text-blue-600 transition-colors group"
                          >
                            <FaUserCircle
                              className="mr-3 text-gray-400 group-hover:text-blue-500 transition-colors"
                              size={16}
                            />
                            <span className="font-medium">My Profile</span>
                          </Link>
                        </motion.div>

                        <div className="border-t border-gray-200 my-1"></div>

                        <motion.div
                          whileHover={{
                            backgroundColor: "rgba(239, 68, 68, 0.05)",
                          }}
                          className="text-red-600"
                        >
                          <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-4 py-3 hover:text-red-700 transition-colors group"
                          >
                            <FaSignOutAlt
                              className="mr-3 text-red-500 group-hover:text-red-600 transition-colors"
                              size={16}
                            />
                            <span className="font-medium">Sign Out</span>
                          </button>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer for fixed navbar */}
      <div className="h-16"></div>
    </React.Fragment>
  );
};

export default AuthNavbar;
